/**
 * DashboardPage — Domain Portfolio View
 *
 * Primary view after login. Shows all domains the user is managing
 * with plan usage, search/filter, guided actions, and domain cards.
 *
 * This IS the domain list — /domains redirects here.
 */

import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { RefreshCw, Globe, Radar, Bot, Sparkles, AlertTriangle } from 'lucide-react'
import { apiService } from '@/services/api'
import { useUserInit } from '@/contexts/UserInitContext'
import { useTeamContext } from '@/contexts/TeamContext'
import { useTierStatus } from '@/hooks/useTierStatus'
import { useEngagementTracking } from '@/hooks/useEngagementTracking'
import { useGreeting } from '@/hooks/useGreeting'
import { useDomain } from '@/hooks/useDomain'
import { GuidedActions } from '@/components/dashboard/GuidedActions'
import { PortfolioGrid, type DomainMonitoringStatus } from '@/components/dashboard/PortfolioGrid'
import { DashboardDriftSummary } from '@/components/dashboard/DashboardDriftSummary'
import {
  DashboardPageSetupLauncher,
  type DashboardFeatureFilter,
} from '@/components/dashboard/DashboardPageSetupLauncher'
import { DashboardSubscriptionNudge } from '@/components/dashboard/DashboardSubscriptionNudge'
import { useFirstWeek, FIRST_WEEK_QUERY_KEY } from '@/hooks/useFirstWeek'
import { FirstWeekPanel } from '@/components/dashboard/first-week/FirstWeekPanel'
import { FirstWeekActions, type StepRequest } from '@/components/dashboard/first-week/FirstWeekActions'
import { FirstWeekBranchAsk } from '@/components/dashboard/first-week/FirstWeekBranchAsk'
import { LiveMoment } from '@/components/dashboard/first-week/LiveMoment'
import { GUIDE_FAILED, liveStepKey } from '@/components/dashboard/first-week/firstWeekCopy'
import { useIntegrations } from '@/hooks/useIntegrations'
import { DsIsland } from '@/design-system/island'
import { connectFromDashboard } from '@/utils/hubspotReturnTo'
import { Button, ButtonLink, Notice, Skeleton } from '@/design-system/primitives'
import { Split, SplitCell } from '@/design-system/domain'
import { NeedsPlan } from '@/design-system/marks'
import '@/design-system/dashboard.css'
import '@/design-system/domain.css'
import type { DomainDetailSummary, FirstWeekStepKey, TeamMember, UnifiedDomain } from '@shared/types'

type FeatureFilter = DashboardFeatureFilter

const BILLING_PLANS_PATH = '/settings/billing?section=plans#subscription-plans'

/* What the HubSpot callback adds to the address when a connection made from the guide comes back here. */
const CONNECT_RETURN_PARAMS = ['connected', 'matched', 'portal']

/* HubSpot connected, to an account that does not run the site the guide is about. */
const connectedWrongAccountLine = (domain: string) =>
  `HubSpot is connected, but ${domain} is not one of that account's websites. Connect the HubSpot account that runs ${domain}.`

const featureFilters: { value: FeatureFilter; label: string; icon: React.ElementType }[] = [
  { value: 'all', label: 'All', icon: Globe },
  { value: 'scan', label: 'Site Scan', icon: Radar },
  { value: 'bot-tracking', label: 'Bot Tracking', icon: Bot },
  { value: 'schema', label: 'Schema', icon: Sparkles },
  { value: 'drift', label: 'Changes', icon: AlertTriangle },
]

export default function DashboardPage() {
  const { isLoaded } = useAuth()
  const { isInitialized, initError, isInitializing, retryInit } = useUserInit()
  const { currentTeam, currentUserRole, teamMembers } = useTeamContext()
  const { isPro, usage: tierUsage, limits: tierLimits, entitlements, accountMode, isLoading: isTierLoading, isEntitlementsKnown } = useTierStatus()
  const trackEngagement = useEngagementTracking()
  const { greeting, subtitle } = useGreeting()
  const { domains, isLoadingDomains, hasDomains } = useDomain()
  const { getIntegrationsForDomain } = useIntegrations()

  // Get current team ID for query key scoping
  const teamId = currentTeam?.team?.id

  // ── Search & Filter ────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [featureFilter, setFeatureFilter] = useState<FeatureFilter>('all')
  const [addDomainOpen, setAddDomainOpen] = useState(false)

  // ── The first week ─────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams()
  const arrivedFromCheckout = searchParams.get('welcome') === 'trial'
  const queryClient = useQueryClient()
  const guideEnabled = isInitialized && !!teamId
  const firstWeek = useFirstWeek({
    teamId,
    enabled: guideEnabled,
    viewedSource: arrivedFromCheckout ? 'checkout' : 'dashboard',
  })
  const [stepRequest, setStepRequest] = useState<StepRequest | null>(null)
  const guide = firstWeek.guide
  /*
    The guide is for a team on the free trial, and for an account with no
    plan that is still getting its first page live. Nobody else.

    It used to also show whenever a step was unfinished, which was meant to
    let somebody who converted mid-trial carry on where they left off. But
    "has an unfinished step" is not "just converted": every paying customer
    from before this guide existed has unfinished steps too, so the panel
    turned up on established accounts asking them to confirm a profile and
    connect a CMS they set up long ago.

    The free variant has the same guard in a different shape: no page set up,
    or exactly one. A free account holding several pages from the old
    allowance was set up long ago and is not walked through it again. The
    total has to be a counted one: an answer that carries none is not an
    answer of none.

    The free guide also needs the page's own word that there is no plan. The
    server decides which guide it is, and it has been wrong about that before,
    for every paying customer at once. So an answer of 'free' is drawn only
    when the plan this page read for itself agrees: known, not a subscriber,
    not a grant, not lapsed, not a credit pack. "Your first page is free" is
    false for every one of those. The trial guide is decided by the trial its
    own answer carries, as before, and does not wait on any of this.

    Known means read, not merely finished loading. A plan request that failed
    is no longer loading, and the hook then falls back to a guessed free tier.
    A guess is not the page's own word, so the free guide waits for a real
    answer.

    An answer that names no guide comes from a server older than the free
    guide, and is the trial's.

    A dismissed guide still draws its one collapsed line.
  */
  const guideVariant = guide?.variant ?? 'trial'
  const pageKnowsThereIsNoPlan =
    isEntitlementsKnown &&
    !isTierLoading &&
    !entitlements.isPaidSubscriber &&
    accountMode !== 'admin_grant' &&
    accountMode !== 'expired_subscription_readonly' &&
    accountMode !== 'legacy_credit_pack'
  const showGuide = guideVariant === 'free'
    ? pageKnowsThereIsNoPlan && typeof guide?.managedPageCount === 'number' && guide.managedPageCount <= 1
    : guide?.trial?.status === 'trialing'
  const guideOpen = showGuide && !!guide && !guide.dismissed
  /*
    Connecting HubSpot from the guide comes back here with what happened in
    the address. The one thing worth saying is that the account connected
    does not run this site. It is read once, as the page arrives, and kept
    here, because the address is cleaned straight after.
  */
  const [connectedWrongAccount, setConnectedWrongAccount] = useState(
    () => searchParams.get('connected') === '1' && searchParams.get('matched') === '0',
  )
  // The teammate title names the person who invited them. The team's member
  // list carries first names; the owner is the one flagged as such.
  const ownerName = useMemo(() => {
    const owner = teamMembers?.members.find((m: TeamMember) => m.isOwner || m.userId === teamMembers.ownerId)
    return owner?.firstName || null
  }, [teamMembers])
  const refetchGuide = () => {
    if (teamId) void queryClient.invalidateQueries({ queryKey: FIRST_WEEK_QUERY_KEY(teamId) })
  }
  const askStep = (key: FirstWeekStepKey) => setStepRequest({ key, at: Date.now() })
  const dismissGuide = async () => {
    // If the record fails the guide simply stays; that is the honest state.
    try {
      await apiService.dismissTour('first-week')
    } catch {
      return
    }
    trackEngagement('first_week_dismissed')
    refetchGuide()
  }

  /*
    What the HubSpot callback left in the address is news, not a place: it is
    taken out, in place, so a refresh or a copied link does not say it again.
    It runs whenever the address changes, so nothing else that rewrites the
    address on the same render can put it back.
  */
  useEffect(() => {
    if (!CONNECT_RETURN_PARAMS.some((name) => searchParams.has(name))) return
    const next = new URLSearchParams(searchParams)
    for (const name of CONNECT_RETURN_PARAMS) next.delete(name)
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    if (!arrivedFromCheckout || !guideEnabled) return
    // Read once, then drop the parameter so a refresh is not a second
    // arrival. It waits for the guide to be askable, so the first fetch is
    // the one that carries where the person came from.
    const next = new URLSearchParams(searchParams)
    next.delete('welcome')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedFromCheckout, guideEnabled])

  // Set page title
  useEffect(() => {
    document.title = 'Super Schema | Domains'
  }, [])

  // ── Domain limits query ────────────────────────────────────────────────────
  const domainLimitsQuery = useQuery({
    queryKey: ['domain-limits', teamId],
    queryFn: () => apiService.getDomainLimits(),
    enabled: isLoaded && isInitialized && !!teamId,
    staleTime: 60_000,
  })

  // ── Organizations (for GuidedActions publisher nudge) ─────────────────────
  const { data: organizationsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => apiService.listOrganizations(),
    enabled: isLoaded && isInitialized,
    staleTime: 60_000,
  })
  const organizations = organizationsData?.data || []

  // ── Parallel domain summary fetching ───────────────────────────────────────
  const summaryQueries = useQueries({
    queries: domains.map((domain) => ({
      queryKey: ['domain-summary', teamId, domain.id],
      queryFn: async () => {
        const res = await apiService.getDomainSummary(domain.id)
        return { domainId: domain.id, summary: res.data }
      },
      enabled: !!teamId && !!domain.id,
      staleTime: 60_000,
    })),
  })

  // Build summaries map from completed queries
  const summaries = useMemo(() => {
    const map = new Map<string, DomainDetailSummary>()
    for (const query of summaryQueries) {
      if (query.data) {
        map.set(query.data.domainId, query.data.summary)
      }
    }
    return map
  }, [summaryQueries])
  const isSummaryLoading = summaryQueries.some((q) => q.isLoading)

  const monitoringStatusQueries = useQueries({
    queries: domains.map((domain) => ({
      queryKey: ['monitoring-status', teamId, domain.id],
      queryFn: async () => {
        const res = await apiService.getMonitoringStatus(domain.id)
        return { domainId: domain.id, status: res.data }
      },
      enabled: !!teamId && !!domain.id && isPro,
      staleTime: 60_000,
    })),
  })

  const monitoringStatuses = useMemo(() => {
    const map = new Map<string, DomainMonitoringStatus>()
    for (const query of monitoringStatusQueries) {
      if (query.data) {
        map.set(query.data.domainId, query.data.status)
      }
    }
    return map
  }, [monitoringStatusQueries])

  // Derived values
  const domainCurrent = domainLimitsQuery.data?.data?.current ?? domains.length
  const domainMax = domainLimitsQuery.data?.data?.max ?? null
  const canManageDomainRegistry = currentUserRole === 'owner' || currentUserRole === 'admin'
  const canManageBilling = canManageDomainRegistry
  const showTrialLimitCallout =
    Boolean(teamId) &&
    isEntitlementsKnown &&
    !isTierLoading &&
    entitlements.upgradeReason === 'trial_page_limit' &&
    !entitlements.canGenerateSchema
  const canAddDomain = canManageDomainRegistry && (domainLimitsQuery.data?.data?.allowed ?? true)
  const canManageBusinessProfile =
    canManageDomainRegistry && String(accountMode) !== 'expired_subscription_readonly'
  const schemaCostMode = entitlements.schemaCostMode
  const shouldShowSubscriptionNudge =
    !isTierLoading &&
    !entitlements.isPaidSubscriber &&
    accountMode !== 'admin_grant' &&
    accountMode !== 'expired_subscription_readonly' &&
    (
      accountMode === 'free_trial' ||
      accountMode === 'legacy_credit_pack' ||
      (schemaCostMode === 'credit' && entitlements.hasPurchasedCredits)
    )
  const subscriptionNudgeVariant =
    accountMode === 'legacy_credit_pack' || (schemaCostMode === 'credit' && entitlements.hasPurchasedCredits)
      ? 'legacy'
      : 'free_trial'
  const subscriptionNudgeMetadata = useMemo(() => ({
    accountMode,
    schemaCostMode,
    teamRole: currentUserRole || 'unknown',
    source: 'dashboard_hero',
  }), [accountMode, currentUserRole, schemaCostMode])

  /*
    While the guide is open it makes the offer itself, in its own first
    line: two asks for a plan on one screen is one too many. The offer also
    waits for the guide to answer, so it never appears and then vanishes.

    "Answered" is more than "not loading". The plan is asked for as soon as
    there is a team; the guide waits for the account to load as well, and a
    request that has not been made yet reports that it is not loading. So the
    offer waits for the guide to have been asked for (`guideEnabled`, the
    same condition its hook fetches on) and then to have settled, whether
    with an answer or an error.
  */
  const guideSettled = guideEnabled && !firstWeek.isLoading
  const drawSubscriptionNudge = shouldShowSubscriptionNudge && guideSettled && !guideOpen

  useEffect(() => {
    if (!drawSubscriptionNudge) return
    trackEngagement('dashboard_subscription_nudge_viewed', subscriptionNudgeMetadata)
  }, [drawSubscriptionNudge, subscriptionNudgeMetadata, trackEngagement])

  // ── Filtered domains ─────────────────────────────────────────────────────
  const filteredDomains = useMemo(() => {
    let result = domains

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((d: UnifiedDomain) =>
        d.domain.toLowerCase().includes(q) ||
        (d.displayName && d.displayName.toLowerCase().includes(q))
      )
    }

    // Feature filter
    if (featureFilter !== 'all') {
      result = result.filter((d: UnifiedDomain) => {
        switch (featureFilter) {
          case 'scan': return d.hasScanData
          case 'bot-tracking': return d.hasBotTracking
          case 'schema': return d.hasSchemaData
          case 'drift': return !!d.openDriftEvents && d.openDriftEvents > 0
          default: return true
        }
      })
    }

    return result
  }, [domains, searchQuery, featureFilter])

  // Domains with open drift — drives the summary banner + the Drift filter chip
  const domainsWithDrift = useMemo(
    () => domains.filter((d: UnifiedDomain) => !!d.openDriftEvents && d.openDriftEvents > 0),
    [domains]
  )

  // Feature counts for filter badges
  const featureCounts = useMemo(() => ({
    all: domains.length,
    scan: domains.filter((d: UnifiedDomain) => d.hasScanData).length,
    'bot-tracking': domains.filter((d: UnifiedDomain) => d.hasBotTracking).length,
    schema: domains.filter((d: UnifiedDomain) => d.hasSchemaData).length,
    drift: domainsWithDrift.length,
  }), [domains, domainsWithDrift])

  const generatedPageLimit = entitlements.generatedPageLimit
  const generatedPageDetail = generatedPageLimit === -1
    ? 'no limit'
    : generatedPageLimit > 0
      ? `of ${generatedPageLimit}`
      : 'included'
  const monitoringIsAvailable = entitlements.isPaidSubscriber

  const isPortfolioStatsLoading = isTierLoading
  const portfolioStats = useMemo(() => ([
    {
      label: 'Sites',
      value: domainCurrent.toLocaleString(),
      detail: domainMax === null ? 'in your workspace' : `of ${domainMax}`,
    },
    {
      label: 'Pages set up',
      value: entitlements.generatedPagesUsed.toLocaleString(),
      detail: generatedPageDetail,
    },
    /*
      A locked cell keeps its place and says where to get the thing. It used to
      print the word "Locked" where the number goes, which tells somebody what
      they cannot have and not what it is.
    */
    monitoringIsAvailable
      ? {
          label: 'Pages watched',
          value: tierUsage.monitoredPages.toLocaleString(),
          detail: tierLimits.monitoredPages === -1 ? 'no limit' : `of ${tierLimits.monitoredPages}`,
        }
      : { label: 'Pages watched', value: null, locked: true },
    monitoringIsAvailable
      ? {
          label: 'Pages that changed',
          value: featureCounts.drift.toLocaleString(),
          detail: featureCounts.drift > 0 ? 'worth a look' : 'nothing to look at',
        }
      : { label: 'Pages that changed', value: null, locked: true },
  ]), [
    domainCurrent,
    domainMax,
    entitlements.generatedPagesUsed,
    featureCounts.drift,
    generatedPageDetail,
    monitoringIsAvailable,
    tierLimits.monitoredPages,
    tierUsage.monitoredPages,
  ])

  // ── Error state ────────────────────────────────────────────────────────────
  if (initError && !isInitialized) {
    return (
      <DashShell>
        <div className="ds-dash__hello">
          <h1 className="ds-h2">{greeting}</h1>
        </div>
        <Notice kind="look">
          <b>We could not load your account</b>
          <p>
            This is almost always temporary. Try again, and if it keeps happening tell us and we
            will look at it from our side.
          </p>
          <div className="ds-dash__driftsites">
            <Button onClick={retryInit} loading={isInitializing}>
              <RefreshCw aria-hidden="true" />
              Try again
            </Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reload the page
            </Button>
          </div>
          {initError.message && <p className="ds-small">{initError.message}</p>}
        </Notice>
      </DashShell>
    )
  }

  // ── Main layout ────────────────────────────────────────────────────────────
  return (
    <DashShell>
      {/*
        The greeting used to sit inside a navy slab under a monospace uppercase
        "DASHBOARD" chip, which named the page somebody had just navigated to.
        It is type on the page now.
      */}
      <div className="ds-dash__hello" data-testid="dashboard-hero">
        <h1 className="ds-h2">{greeting}</h1>
        <p className="ds-lead">{subtitle}</p>
      </div>

      {showTrialLimitCallout && (
        <section className="ds-card" aria-labelledby="trial-limit-heading">
          <div className="ds-card__body ds-dash__offer">
            <div>
              <h2 id="trial-limit-heading" className="ds-h4">You’ve used your 20 trial pages</h2>
              <p>
                {canManageBilling
                  ? 'Ready for more? Start your paid subscription to keep setting up pages with your plan.'
                  : 'Ask a workspace owner or admin to start a paid subscription so you can keep setting up pages.'}
              </p>
            </div>
            {canManageBilling && (
              <ButtonLink to="/settings/billing#start-paid-subscription">
                Start paid subscription
              </ButtonLink>
            )}
          </div>
        </section>
      )}

      {/* The first week sits above the numbers: it is what a new customer
          came here to do. It is a guest on the page, so when it cannot load
          it says so in one line and the rest of the page stands. */}
      {firstWeek.isError && <Notice kind="look">{GUIDE_FAILED}</Notice>}
      {/* Only beside the guide, which is where the connection was made from:
          the address alone never puts this in front of anybody else. */}
      {connectedWrongAccount && showGuide && guide?.domain && (
        <Notice kind="look">
          <p>{connectedWrongAccountLine(guide.domain.domain)}</p>
          {/* The guide's HubSpot step already reads done (any connection
              counts), so the way to connect the right account is here. */}
          <div className="ds-notice__acts">
            <ButtonLink size="sm" to={connectFromDashboard(guide.domain.domain)}>
              Connect HubSpot
            </ButtonLink>
            <Button variant="secondary" size="sm" onClick={() => setConnectedWrongAccount(false)}>
              Got it
            </Button>
          </div>
        </Notice>
      )}
      {showGuide && guide && (
        <>
          {guide.liveUrl && guide.steps.some((s) => s.key === liveStepKey(guide.variant) && s.state === 'done') && (
            <LiveMoment urlId={guide.liveUrl.urlId} url={guide.liveUrl.url} />
          )}
          {guide.viewer === 'owner' && guide.branch === 'unknown' && !guide.dismissed && (
            <FirstWeekBranchAsk onAnswered={refetchGuide} />
          )}
          <FirstWeekPanel guide={guide} onStep={askStep} onDismiss={dismissGuide} ownerName={ownerName} />
          <FirstWeekActions
            guide={guide}
            request={stepRequest}
            onChanged={refetchGuide}
            onHandled={() => setStepRequest(null)}
            onAddSite={() => setAddDomainOpen(true)}
          />
        </>
      )}

      <div data-testid="dashboard-hero-stats-grid">
        <Split>
          {isPortfolioStatsLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <SplitCell
                  key={`dashboard-stat-skeleton-${index}`}
                  label={
                    <span data-testid="dashboard-stat-skeleton">
                      <Skeleton width={78} height={11} />
                    </span>
                  }
                  value={<Skeleton width={52} height={22} />}
                  note={<Skeleton width={64} height={11} />}
                />
              ))
            : portfolioStats.map((stat) => (
                <SplitCell
                  key={stat.label}
                  label={stat.label}
                  value={'locked' in stat && stat.locked ? <NeedsPlan plan="Starter" /> : stat.value}
                  note={'detail' in stat ? stat.detail : undefined}
                />
              ))}
        </Split>
      </div>

      {drawSubscriptionNudge && (
        <DashboardSubscriptionNudge
          variant={subscriptionNudgeVariant}
          canManageBilling={canManageBilling}
          billingPlansPath={BILLING_PLANS_PATH}
          onViewPlansClick={() => trackEngagement('dashboard_subscription_nudge_clicked', subscriptionNudgeMetadata)}
        />
      )}

      {/* A page that changed under somebody is the one thing that outranks the
          rest of this screen, so it sits above all of it. */}
      {hasDomains && domainsWithDrift.length > 0 && (
        <DashboardDriftSummary
          affectedDomains={domainsWithDrift.map((d) => ({
            domain: d.domain,
            displayName: d.displayName,
            openDriftEvents: d.openDriftEvents,
          }))}
          onReviewAll={() => setFeatureFilter('drift')}
        />
      )}

      <DashboardPageSetupLauncher
        domains={domains}
        isLoadingDomains={isLoadingDomains}
        canAddDomain={canAddDomain}
        canManageDomainRegistry={canManageDomainRegistry}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        featureFilter={featureFilter}
        onFeatureFilterChange={setFeatureFilter}
        featureFilters={featureFilters}
        featureCounts={featureCounts}
        filteredCount={filteredDomains.length}
        totalCount={domains.length}
        addDomainOpen={addDomainOpen}
        onAddDomainOpenChange={setAddDomainOpen}
      />

      {/* While the guide is open it is the one list of things to do; the
          action queue comes back once the guide is hidden or finished. */}
      {hasDomains && !guideOpen && (
        <GuidedActions
          domains={domains}
          summaries={summaries}
          organizations={organizations}
          isPro={isPro}
          canManageBusinessProfile={canManageBusinessProfile}
          isLoading={isLoadingDomains || isSummaryLoading}
        />
      )}

      <PortfolioGrid
        domains={filteredDomains}
        summaries={summaries}
        monitoringStatuses={monitoringStatuses}
        isSummaryLoading={isSummaryLoading}
        isLoadingDomains={isLoadingDomains}
        isPro={isPro}
        canAddDomain={canAddDomain}
        organizations={organizations}
        canManageBusinessProfile={canManageBusinessProfile}
        getIntegrationsForDomain={getIntegrationsForDomain}
      />
    </DashShell>
  )
}

/**
 * The ground the whole dashboard stands on.
 *
 * `app-dark-surface-scope` stays while the cards inside it are still legacy;
 * what it no longer paints is cream, which was the old palette showing through
 * under this system's cool white.
 */
function DashShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-testid="dashboard-page-shell"
      className="app-dark-surface-scope ds-shell-ground min-h-full flex-1 text-ink-deep-navy transition-colors dark:text-foreground"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
        <DsIsland panel={false} ground={false} className="ds-dash">
          {children}
        </DsIsland>
      </div>
    </div>
  )
}
